import type { ReactNode } from "react";
import styles from "./SectionHeading.module.css";

/**
 * A section heading, in the shape `contracts/DESIGN-TOKENS.md` § Shared component patterns
 * fixes for every repo: the title, a 2px brass rule directly under it, and an optional line
 * of description below that.
 *
 * There is no eyebrow. The desk carried one — a brass mono caption above every title, over a
 * `boardRule` hairline — from `APP-001` until `APP-014`, which is longer than `dealbid-web`
 * carried it: `WEB-017` withdrew it there when the design canvas turned out to have none, and
 * nothing held the pattern for this repo until `CANON-003` did. Against a ruled title the
 * eyebrow spends brass twice on one heading and opens every section with two competing
 * labels.
 *
 * Withdrawing the device did not delete what it said. Every eyebrow that named a *fact* — the
 * display day an auction buys, the day a mention runs, the timezone a column of dates is in —
 * moved to {@link meta}, which is where that kind of statement belongs and where the board
 * puts the same thing. The ones that only repeated their own title went.
 */
export function SectionHeading({
  title,
  description,
  id,
  level = 2,
  meta,
}: {
  title: string;
  description?: ReactNode | undefined;
  id?: string | undefined;
  level?: 2 | 3 | undefined;
  /**
   * A factual line beside the title — the display day, the timezone the dates below are in.
   * It states a fact about the thing the title names, which is why it sits on the title's
   * own baseline rather than under it, where the description's prose goes.
   */
  meta?: ReactNode | undefined;
}) {
  const Title = level === 2 ? "h2" : "h3";
  return (
    <div className={styles.heading}>
      <div className={styles.titleRow}>
        <Title id={id} className={styles.title}>
          {title}
        </Title>
        {meta ? <p className={`${styles.meta} figure`}>{meta}</p> : null}
      </div>
      {description ? <p className={styles.description}>{description}</p> : null}
    </div>
  );
}
